import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
} from "@mui/material";
import { ExpandMore, Speed } from "@mui/icons-material";
import JSONPretty from "react-json-pretty";

interface ExecutionEventProps {
  execution: {
    model: string;
    latencySeconds: number;
    inputMessageCount: number;
    metadataJson: string;
  };
  index: number;
  combinedIndex: number;
}

const formatTokens = (n: number): string => {
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return String(n);
};

export default ({ execution, index, combinedIndex }: ExecutionEventProps) => {
  let parsedMetadata: any;
  let metadataParseError = false;
  try {
    parsedMetadata = JSON.parse(execution.metadataJson || "{}");
  } catch {
    parsedMetadata = execution.metadataJson;
    metadataParseError = true;
  }

  const usage = parsedMetadata?.usage || {};
  const inputTokens: number = usage.input_tokens || 0;
  const outputTokens: number = usage.output_tokens || 0;
  const cacheRead: number = usage.cache_read_input_tokens || 0;
  const cacheCreation: number = usage.cache_creation_input_tokens || 0;
  const systemStaticChars: number = parsedMetadata?.system_static_chars || 0;
  const systemDynamicChars: number = parsedMetadata?.system_dynamic_chars || 0;
  const messagesChars: number = parsedMetadata?.messages_chars || 0;

  return (
    <Accordion
      key={`execution-${combinedIndex}`}
      sx={{
        marginBottom: "8px !important",
        marginTop: "0px !important",
        ".MuiPaper-root, .MuiPaper-root.Mui-expanded": {
          marginBottom: "8px !important",
          marginTop: "0px !important",
        },
        backgroundColor: "#f5f5f5",
        borderColor: "#e0e0e0",
      }}
    >
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            width: "100%",
            flexWrap: "wrap",
          }}
        >
          <Speed sx={{ color: "#1976d2" }} />
          <Typography sx={{ flexGrow: 1 }}>
            <b>Execution {index + 1}</b>
          </Typography>
          {inputTokens > 0 && (
            <Chip
              label={`↓ ${formatTokens(inputTokens)} in`}
              size="small"
              sx={{
                backgroundColor: "#E3F2FD",
                color: "#1565C0",
                fontSize: "11px",
              }}
            />
          )}
          {outputTokens > 0 && (
            <Chip
              label={`↑ ${formatTokens(outputTokens)} out`}
              size="small"
              sx={{
                backgroundColor: "#E8F5E9",
                color: "#2E7D32",
                fontSize: "11px",
              }}
            />
          )}
          {cacheRead > 0 && (
            <Chip
              label={`⚡ ${formatTokens(cacheRead)} cached`}
              size="small"
              sx={{
                backgroundColor: "#FFF3E0",
                color: "#E65100",
                fontSize: "11px",
              }}
            />
          )}
          {cacheCreation > 0 && (
            <Chip
              label={`📦 ${formatTokens(cacheCreation)} stored`}
              size="small"
              sx={{
                backgroundColor: "#F3E5F5",
                color: "#6A1B9A",
                fontSize: "11px",
              }}
            />
          )}
          <Chip
            label={execution.model || "Unknown Model"}
            size="small"
            variant="outlined"
          />
          <Chip
            label={`${(execution.latencySeconds || 0).toFixed(2)}s`}
            size="small"
            color="secondary"
            variant="outlined"
          />
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Box sx={{ overflow: "auto" }}>
          {(systemStaticChars > 0 ||
            systemDynamicChars > 0 ||
            messagesChars > 0) && (
            <Box sx={{ mb: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
              <Typography variant="bodySmallHeavy" sx={{ width: "100%" }}>
                Input breakdown (chars sent)
              </Typography>
              {systemStaticChars > 0 && (
                <Chip
                  label={`Static instructions: ${systemStaticChars.toLocaleString()} chars`}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: "11px" }}
                />
              )}
              {systemDynamicChars > 0 && (
                <Chip
                  label={`Family context: ${systemDynamicChars.toLocaleString()} chars`}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: "11px" }}
                />
              )}
              {messagesChars > 0 && (
                <Chip
                  label={`History: ${messagesChars.toLocaleString()} chars`}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: "11px" }}
                />
              )}
            </Box>
          )}
          <Typography variant="bodySmallHeavy" gutterBottom>
            Execution Metadata
          </Typography>
          {metadataParseError ? (
            <Typography component="pre" sx={{ whiteSpace: "pre-wrap" }}>
              {execution.metadataJson}
            </Typography>
          ) : (
            <JSONPretty
              id={`execution-metadata-${combinedIndex}`}
              data={parsedMetadata}
              theme={{
                main: "line-height:1.3;color:#333333;background:#fafafa;overflow:auto;padding:8px;border-radius:4px;",
                error:
                  "line-height:1.3;color:#d32f2f;background:#fafafa;overflow:auto;",
                key: "color:#1976d2;",
                string: "color:#388e3c;",
                value: "color:#7b1fa2;",
                boolean: "color:#f57c00;",
              }}
            />
          )}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};
